import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BookmarkPlus } from "lucide-react";
import { toast } from "sonner";

// Copies a platform technique/sequence into the user's own library.
// kind: "technique" | "sequence"
export default function AddToLibraryDialog({ open, onOpenChange, item, kind, onDone }) {
  const { user } = useAuth();
  const canGym = (user?.role === "admin" || user?.role === "coach") && user?.gym_id;
  const [scope, setScope] = useState("personal");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setScope("personal"); }, [open]);
  if (!item) return null;

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/${kind}s/${item.id}/copy`, { scope });
      toast.success(`"${item.name}" added to your ${scope === "gym" ? "gym" : "personal"} library`);
      onDone?.();
      onOpenChange(false);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-md" data-testid="add-to-library-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <BookmarkPlus className="h-5 w-5 text-primary" /> Add to Library
          </DialogTitle>
          <DialogDescription>
            This creates your own independent copy of “{item.name}”. Changes you make won’t affect the 🌐 Platform version, and platform updates won’t change your copy.
          </DialogDescription>
        </DialogHeader>
        <div className="py-2">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Add to</Label>
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger data-testid="add-to-library-scope-select" className="mt-1.5 bg-background border-border"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="personal">👤 Personal (private)</SelectItem>
              {canGym && <SelectItem value="gym">🏫 Gym (shared)</SelectItem>}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="confirm-add-to-library" onClick={submit} disabled={saving}
            className="bg-primary hover:bg-primary/90 text-white">
            {saving ? "Adding…" : "Add to Library"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
